import { HttpClient, HttpParams } from '@angular/common/http';

/** Origem da mudança de responsável — ver `CreateAgentOnLeadDto` no back-end. */
export type LeadAgentAssignmentSource = 'LEAD' | 'DEAL';

/** Filtros da exportação de leads para Excel — ver `ExportLeadsQueryDto` no back-end. */
export interface LeadsExportFilters {
  search?: string | null;
  range?: { from: Date; to: Date } | null;
  sources?: any[] | null;
  stores?: any[] | null;
  agents?: any[] | null;
  leadTypes?: any[] | null;
  includeConverted: boolean;
  includeArchived: boolean;
}

export interface LeadsExportCount {
  total: number;
  limit: number;
  exceedsLimit: boolean;
}

/** Importação de leads por Excel — espelho de `import-leads.dto.ts` no back-end. */
export type LeadsImportTarget =
  | 'name'
  | 'email'
  | 'phone'
  | 'phone2'
  | 'leadType'
  | 'source'
  | 'portal'
  | 'agent'
  | 'propertyReference'
  | 'createdAt'
  | 'message';

export type LeadsImportEnumTarget = 'leadType' | 'source' | 'portal' | 'agent';

export interface LeadsImportPreview {
  sheetName: string;
  columnCount: number;
  columnLetters: string[];
  headers: string[];
  sampleRows: string[][];
  totalRows: number;
  limit: number;
  distinctValues: Record<number, { values: string[]; truncated: boolean }>;
  fileSha256: string;
}

export interface LeadsImportMapping {
  hasHeader: boolean;
  columns: { index: number; target: LeadsImportTarget }[];
  fixed?: {
    leadType?: 'BUYER' | 'OWNER';
    source?: number;
    portal?: number;
    agent?: string;
  };
  /** Na origem, um valor pode ser `{ source, portal }` quando a origem é PORTAL. */
  valueMaps?: Partial<Record<LeadsImportEnumTarget, Record<string, any>>>;
  options?: { skipExistingOpenLead?: boolean };
}

export interface LeadsImportRowIssue {
  key: string;
  params?: Record<string, string | number>;
}

export interface LeadsImportRowResult {
  row: number;
  status: 'ok' | 'skip' | 'error';
  reasons: LeadsImportRowIssue[];
  warnings: LeadsImportRowIssue[];
  name?: string;
  email?: string;
  phone?: string;
  existingLeadId?: number;
  leadId?: number;
  contactId?: number;
  contactCreated?: boolean;
}

export interface LeadsImportReport {
  dryRun: boolean;
  totalRows: number;
  blankRows: number;
  ok: number;
  skipped: number;
  errors: number;
  created: number;
  contactsCreated: number;
  contactsReused: number;
  rows: LeadsImportRowResult[];
}
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LeadHistory, LeadsModel } from '../models/leads.model';
import { CookieService } from 'ngx-cookie-service';
import {
  ExternalPropertyModel,
  ExternalPropertyPayloadModel,
} from 'src/app/shared/models/external-property.model';

@Injectable({
  providedIn: 'root',
})
export class LeadsService {
  constructor(
    private _cookiesService: CookieService,
    private http: HttpClient,
  ) { }

  private getExternalPropertyTypeId(property: any): number | null {
    const propertyTypeId = Number(
      property?.propertyTypeGroupId ?? property?.propertyTypeId ?? NaN,
    );

    return Number.isFinite(propertyTypeId) ? propertyTypeId : null;
  }

  private getExternalPropertyTypeDescription(property: any): string | null {
    return (
      property?.propertyTypeGroup?.propertyTypeGroupTranslations?.[0]
        ?.description ??
      property?.propertyTypeGroup?.description ??
      property?.propertyType ??
      null
    );
  }

  private getExternalPropertyTitle(property: any): string {
    const propertyType =
      this.getExternalPropertyTypeDescription(property) ?? "";
    const typology = `${property?.typology ?? ""}`.trim();
    const title = [propertyType, typology].filter(Boolean).join(" ").trim();

    return title || property?.reference || "Imóvel Externo";
  }

  private mapLeadProperty(data: any) {
    if (data?.externalProperty) {
      return {
        id: data.externalProperty.id,
        ref: data.externalProperty.reference,
        reference: data.externalProperty.reference,
        title: this.getExternalPropertyTitle(data.externalProperty),
        price: data.externalProperty.price,
        symbol: "€",
        image: undefined,
        isExternal: true,
        externalPropertyId: data.externalProperty.id,
        propertyTypeId: this.getExternalPropertyTypeId(data.externalProperty),
        propertyType: this.getExternalPropertyTypeDescription(
          data.externalProperty,
        ),
        typology: data.externalProperty.typology ?? null,
        agency: data.externalProperty.agency ?? null,
        address: data.externalProperty.address ?? null,
        zipCode: data.externalProperty.zipCode ?? null,
        districtId: data.externalProperty.districtId ?? null,
        countyId: data.externalProperty.countyId ?? null,
        parishId: data.externalProperty.parishId ?? null,
        agentId: data.externalProperty.agentId ?? null,
        agentName: data.externalProperty.agent?.name ?? null,
        location: {
          address: data.externalProperty.address ?? null,
          zipCode: data.externalProperty.zipCode ?? null,
          district: data.externalProperty.district?.name ?? null,
          county: data.externalProperty.county?.name ?? null,
          parish: data.externalProperty.parish?.name ?? null,
        },
      };
    }

    if (!data?.property) {
      return undefined;
    }

    return {
      id: data.property.id,
      ref: data.property.reference,
      reference: data.property.reference,
      title: data.property.propertyTranslations?.[0]?.title,
      price: data.property.price,
      symbol: data.property.currency?.symbol,
      image: data.property.propertyFiles?.[0]?.fileName,
      location: {
        district: data.property.propertyAddress?.[0]?.district?.name,
        county: data.property.propertyAddress?.[0]?.county?.name,
        parish: data.property.propertyAddress?.[0]?.parish?.name,
      },
    };
  }

  private mapExternalProperty(data: any): ExternalPropertyModel | undefined {
    if (!data?.externalProperty) {
      return undefined;
    }

    return {
      id: data.externalProperty.id,
      ref: data.externalProperty.reference,
      reference: data.externalProperty.reference,
      title:
        this.getExternalPropertyTitle(data.externalProperty) ||
        'Imóvel Externo',
      price: data.externalProperty.price,
      symbol: '€',
      image: undefined,
      isExternal: true,
      propertyTypeId: this.getExternalPropertyTypeId(data.externalProperty),
      propertyType: this.getExternalPropertyTypeDescription(
        data.externalProperty,
      ),
      typology: data.externalProperty.typology ?? null,
      agency: data.externalProperty.agency ?? null,
      address: data.externalProperty.address ?? null,
      zipCode: data.externalProperty.zipCode ?? null,
      districtId: data.externalProperty.districtId ?? null,
      countyId: data.externalProperty.countyId ?? null,
      parishId: data.externalProperty.parishId ?? null,
      agentId: data.externalProperty.agentId ?? null,
      agentName: data.externalProperty.agent?.name ?? null,
      location: {
        district: data.externalProperty.district?.name ?? null,
        county: data.externalProperty.county?.name ?? null,
        parish: data.externalProperty.parish?.name ?? null,
      },
    };
  }

  /**
   * Uma lead pelo id, no âmbito do utilizador, já com a forma da lista.
   * Devolve `null` quando não existe ou não é visível.
   */
  getOne(id: number, lang: string): Observable<LeadsModel | null> {
    return this.http
      .get<any>(`${environment.apiUrl}/leads/detail/${id}/${lang || 'pt'}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => (data ? this.mapDataLeads(data) : null)),
        catchError(() => of(null)),
      );
  }

  /***
   * Get All Agents
   */
  getAll(page: number, search = '', type: any, date: any, filters: any = {}) {
    let params = new HttpParams().set('page', page);
    params = params.set('type', type);
    if (search !== '') params = params.set('search', search);
    if (date?.to && date?.from) {
      let from = date.from.toISOString().split('T')[0];
      params = params.set('from', from);
      const to = date.to.toISOString().split('T')[0];
      params = params.set('to', to);
    }
    if (filters?.sources) {
      params = params.set('sources', filters.sources);
    }
    if (filters?.stores) {
      params = params.set('stores', filters.stores);
    }
    if (filters?.agents) {
      params = params.set('agents', filters.agents);
    }
    if (filters?.leadTypes) {
      params = params.set('leadTypes', filters.leadTypes);
    }
    return this.http
      .get<LeadsModel[]>(`${environment.apiUrl}/leads`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapaDataListLeads(data)),
        catchError((error) => {
          return of({ leads: [], total: 0, unread: 0 });
        }),
      );
  }

  /**
   * Parâmetros comuns às duas rotas de exportação (`export/count` e
   * `export/excel`). As datas seguem a mesma conversão de `getAll`, para a
   * contagem bater com o que a lista mostra.
   */
  private buildLeadsExportParams(filters: LeadsExportFilters): HttpParams {
    let params = new HttpParams();
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.range?.from && filters?.range?.to) {
      params = params.set('from', filters.range.from.toISOString().split('T')[0]);
      params = params.set('to', filters.range.to.toISOString().split('T')[0]);
    }
    const lists: [string, any[] | null | undefined][] = [
      ['sources', filters?.sources],
      ['stores', filters?.stores],
      ['agents', filters?.agents],
      ['leadTypes', filters?.leadTypes],
    ];
    for (const [key, values] of lists) {
      if (values?.length) params = params.set(key, values.join(','));
    }
    params = params.set('includeConverted', String(filters?.includeConverted !== false));
    params = params.set('includeArchived', String(filters?.includeArchived === true));
    return params;
  }

  countLeadsExport(filters: LeadsExportFilters): Observable<LeadsExportCount> {
    const params = this.buildLeadsExportParams(filters);
    return this.http
      .get<LeadsExportCount>(`${environment.apiUrl}/leads/export/count`, { params, withCredentials: true })
      .pipe(first());
  }

  exportLeadsExcel(filters: LeadsExportFilters, columns: string[]): Observable<Blob> {
    let params = this.buildLeadsExportParams(filters);
    if (columns?.length) params = params.set('columns', columns.join(','));
    return this.http
      .get(`${environment.apiUrl}/leads/export/excel`, { params, responseType: 'blob', withCredentials: true })
      .pipe(first());
  }

  /**
   * Importação por Excel. O ficheiro vai em todos os pedidos (o servidor não
   * guarda estado); o mapeamento segue como JSON num campo do multipart.
   */
  previewLeadsImport(file: File): Observable<LeadsImportPreview> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<LeadsImportPreview>(`${environment.apiUrl}/leads/import/preview`, formData, { withCredentials: true })
      .pipe(first());
  }

  validateLeadsImport(file: File, mapping: LeadsImportMapping, fileSha256?: string): Observable<LeadsImportReport> {
    return this.http
      .post<LeadsImportReport>(`${environment.apiUrl}/leads/import/validate`, this.buildLeadsImportFormData(file, mapping, fileSha256), { withCredentials: true })
      .pipe(first());
  }

  commitLeadsImport(file: File, mapping: LeadsImportMapping, fileSha256?: string): Observable<LeadsImportReport> {
    return this.http
      .post<LeadsImportReport>(`${environment.apiUrl}/leads/import/commit`, this.buildLeadsImportFormData(file, mapping, fileSha256), { withCredentials: true })
      .pipe(first());
  }

  private buildLeadsImportFormData(file: File, mapping: LeadsImportMapping, fileSha256?: string): FormData {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('mapping', JSON.stringify(mapping));
    if (fileSha256) formData.append('fileSha256', fileSha256);
    return formData;
  }

  /***
   * Get All Agents
   */
  getLeadsByProperty(propertyId: any, page: number) {
    let params = new HttpParams().set('page', page);

    return this.http
      .get<LeadsModel[]>(`${environment.apiUrl}/leads/property/${propertyId}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapaDataListLeads(data)),
        catchError((error) => {
          return of({ leads: [], total: 0, unread: 0 });
        }),
      );
  }

  /***
   * Get total leads
   */
  getTotal() {
    return this.http.get<any>(`${environment.apiUrl}/leads/total`, {
      withCredentials: true,
    });
  }

  /**
   * archive
   * @param data
   */
  updateToArchive(data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/leads/archive`,
        { leads: data },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   *
   * @param data
   * @returns boolean
   */
  updateToUnarchive(data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/leads/unarchive`,
        { leads: data },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * READ
   * @param data
   */
  updateToRead(id: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/leads/read/${id}`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  createDealByLead(id: any, body: { propertyIds?: string[] } = {}) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/leads/deal/${id}`,
        body,
        { withCredentials: true },
      )
      .pipe(
        first(),
        catchError((error) => {
          return of(null);
        }),
      );
  }

  mapaDataListLeads(data: any) {
    const leads = data.leads.map((l: any) => {
      return this.mapDataLeads(l);
    });
    return { leads, total: data.total, unread: data.unread };
  }

  /**
   * Utilizadores que podem ficar responsáveis por um lead/negócio, já no
   * âmbito do utilizador autenticado (loja do STORE OPERATOR, equipa do
   * COMMERCIAL DIRECTOR). `source` indica a permissão a exigir:
   * CHANGE_AGENT_LEADS (leads) ou CHANGE_AGENT_DEALS (negócios).
   */
  getUsers(search: string = '', source: LeadAgentAssignmentSource = 'LEAD') {
    let params = new HttpParams();
    if (search !== '') params = params.set('search', search);
    if (source !== 'LEAD') params = params.set('source', source);
    return this.http
      .get<any>(`${environment.apiUrl}/leads/all-users`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ users: [], total: 0 });
        }),
      );
  }

  /**
   * update agent on lead
   * @param data
   */
  updateAgentOnLead(data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/leads/update-agent`, data, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((response) => ({
          ...response,
          history: response?.history
            ? this.mapLeadHistory(response.history)
            : undefined,
        })),
      );
  }

  /**
   *
   * @returns all actions
   * @description get all actions for lead history
   */
  getActions() {
    return this.http
      .get<any>(`${environment.apiUrl}/leads/actions`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  getLeadsData() {
    return this.http
      .get<{ actions: { id: any, icon: any, translateKey: any }[], sources: { id: any, name: any, translateKey: any }[], portals: { id: any, name: any }[], stores: any[], agents: any[], propertyTypes: any[], locations?: any[] }>(`${environment.apiUrl}/leads/leads-data`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ actions: [], sources: [], portals: [], stores: [], agents: [], propertyTypes: [], locations: [] });
        }),
      );
  }

  addActivity(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/leads/lead-history`, data, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapLeadHistory(data)),
        // Sem `catchError(() => of(null))`: engolir o erro aqui deixava o
        // utilizador com "Erro ao criar" genérico, quando o servidor tinha
        // dito exactamente o que faltava. Quem chama trata o `error`.
      );
  }

  /** `notes` é o feedback do consultor; só é gravado ao marcar como feita. */
  markDone(id: any, notes?: string | null) {
    return this.http.patch<any>(
      `${environment.apiUrl}/leads/history-done/${id}`,
      { notes: notes ?? null },
      { withCredentials: true },
    );
  }

  deleteActivity(id: any) {
    return this.http.delete<any>(
      `${environment.apiUrl}/leads/lead-history/${id}`,
      { withCredentials: true },
    );
  }

  createManualLead(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/leads/manual`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  changeLeadType(id: any, to: any) {
    return this.http.patch<any>(
      `${environment.apiUrl}/leads/change-type/${id}`,
      { changeTo: to },
      { withCredentials: true },
    );
  }

  addPropertyToLead(
    propertyId: any,
    leadId: any,
    externalProperty?: ExternalPropertyPayloadModel,
  ) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/leads/add-property/${leadId}`,
        { propertyId, externalProperty },
        { withCredentials: true },
      )
      .pipe(first());
  }

  // changeStatus(id: any, data: any, type: "sale" | "listing") {
  //   const lang = this._cookiesService.get("lang");

  //   return this.http
  //     .patch<any>(
  //       `${environment.apiUrl}/leads/change-status/${id}/${lang}?type=${type}`,
  //       data,
  //       { withCredentials: true }
  //     )
  //     .pipe(first());
  // }

  /** `networkScope`: abre à rede toda; por defeito só as agências do utilizador. */
  getAllProperties(page: number, search = '', networkScope = false) {
    const lang = this._cookiesService.get('lang');
    let params = new HttpParams().set('page', page);
    if (search !== '') params = params.set('search', search);
    if (networkScope) params = params.set('scope', 'network');
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/all-properties/${lang}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          return {
            properties:
              data.properties.map((p: any) => {
                return {
                  id: p.id,
                  title: p.title,
                  reference: p.reference,
                  propertyType: p.propertyType,
                  businessType: p.businessType,
                  price: p.price,
                  isSelected: false,
                  isVisible: true,
                  file: `${environment.apiUrl}${environment.dirPropertiesImages}/${p.file}`,
                };
              }) || [],
            total: data.total,
          };
        }),
        catchError((error) => {
          return of({ properties: [], total: 0 });
        }),
      );
  }

  mapDataLeads(data: any): LeadsModel {
    const history = data?.leadHistory.filter(
      (l: any) => l?.responsibleChange || l?.isDone === true || l?.action?.translateKey === 'note',
    );
    const nextActivities = data?.leadHistory.filter(
      (l: any) => !l?.responsibleChange && l?.isDone === false && l?.action?.translateKey !== 'note',
    );

    // Calcula o updatedAt mais recente entre o lead e o history
    const allUpdatedAts = [data?.updatedAt, ...(data?.leadHistory?.map((l: any) => l?.createdAt) || [])]
      .filter(Boolean)
      .map((d: any) => new Date(d));
    const mostRecentUpdatedAt = allUpdatedAts.length > 0 ? new Date(Math.max(...allUpdatedAts.map(d => d.getTime()))) : undefined;

    return {
      id: data?.id,
      email: data?.email,
      name: data?.name,
      message: data?.message,
      phone: data?.phone,
      dateHour: data?.createdAt,
      unread: data?.unread,
      contactType: data?.contactType?.description,
      contactTypeTranslation:
        data?.contactType?.contactTypeTranslations[0]?.description,
      // agent: data?.user?.email,
      agent: {...data?.user, store: data?.user?.usersStores[0]?.store?.name},
      isArchived: data?.isArchived,
      isSelected: false,
      property: data?.externalProperty ? undefined : this.mapLeadProperty(data),
      externalProperty: this.mapExternalProperty(data),
      leadHistory:
        history?.map((l: any) => {
          return {
            id: l?.id,
            action: l?.action,
            startDate: l?.startDate,
            endDate: l?.endDate,
            isDone: l?.isDone,
            doneDate: l?.doneDate,
            title: l?.title,
            notes: l?.notes,
            location: l?.location,
            updatedBy: l?.userUpdated?.name,
            updatedAt: l?.responsibleChange?.changedAt ?? l?.createdAt,
            responsibleChange: l?.responsibleChange
              ? {
                changedAt: l?.responsibleChange?.changedAt,
                from: l?.responsibleChange?.from ?? null,
                to: l?.responsibleChange?.to ?? null,
              }
              : undefined,
          };
        }) ?? [],
      // Origem PORTAL (mesmo sem translateKey na base): com portal associado
      // mostra "{{nome do portal}} (Portal)", sem portal mostra o genérico.
      source:
        data?.leadSource?.translateKey === 't-sources.t-portal' ||
        `${data?.leadSource?.name ?? ''}`.trim().toUpperCase() === 'PORTAL'
          ? data?.portal
            ? 't-sources.t-portal'
            : 't-sources.t-portal-without-name'
          : data?.leadSource?.translateKey || data?.leadSource?.name,
      portal: data?.portal ? data?.portal?.name : undefined,
      nextActivities:
        nextActivities?.map((l: any) => {
          return {
            id: l?.id,
            action: l?.action,
            startDate: l?.startDate,
            endDate: l?.endDate,
            isDone: l?.isDone,
            title: l?.title,
            doneDate: l?.doneDate,
            notes: l?.notes,
            location: l?.location,
            updatedBy: l?.userUpdated?.name,
            updatedAt: l?.responsibleChange?.changedAt ?? l?.createdAt,
            responsibleChange: l?.responsibleChange
              ? {
                changedAt: l?.responsibleChange?.changedAt,
                from: l?.responsibleChange?.from ?? null,
                to: l?.responsibleChange?.to ?? null,
              }
              : undefined,
          };
        }) ?? [],
      leadType: data?.leadType,
      updatedAt: mostRecentUpdatedAt ? mostRecentUpdatedAt : undefined
    };
  }

  mapLeadHistory(data: any): LeadHistory {
    return {
      id: data?.id,
      action: data?.action,
      startDate: data?.startDate,
      endDate: data?.endDate,
      title: data?.title,
      isDone: data?.isDone,
      doneDate: data?.doneDate,
      notes: data?.notes,
      location: data?.location,
      updatedBy: data?.userUpdated?.name,
      updatedAt: data?.responsibleChange?.changedAt ?? data?.createdAt,
      responsibleChange: data?.responsibleChange
        ? {
          changedAt: data?.responsibleChange?.changedAt,
          from: data?.responsibleChange?.from ?? null,
          to: data?.responsibleChange?.to ?? null,
        }
        : undefined,
    };
  }
}
